import React, { useState } from 'react';
import { auth, db, googleProvider } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

const LEVARE_ADMIN_EMAIL = 'contact@levarewellbeing.com';

const getErrorMessage = (code) => {
  switch (code) {
    case 'auth/email-already-in-use': return 'An account with this email already exists.';
    case 'auth/invalid-email': return 'Please enter a valid email address.';
    case 'auth/wrong-password': return 'Incorrect password. Please try again.';
    case 'auth/user-not-found': return 'No account found with this email.';
    case 'auth/too-many-requests': return 'Too many attempts. Please try again later.';
    case 'auth/weak-password': return 'Password must be at least 8 characters.';
    case 'auth/invalid-credential': return 'Invalid email or password.';
    default: return 'Something went wrong. Please try again.';
  }
};

async function lookupCompanyCode(code) {
  const upper = code.trim().toUpperCase();
  try {
    // Look up code in the codeLookup collection — one doc per code, set by LevareAdmin
    const snap = await getDoc(doc(db, 'codeLookup', upper));
    if (snap.exists()) {
      return snap.data(); // { role, companyCode, companyName }
    }
  } catch (e) {
    console.error('lookupCompanyCode error:', e);
    throw new Error('Unable to verify company code. Please try again.');
  }
  return null;
}

const groupStyle = { display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' };
const labelStyle = { fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7a9ab8' };
const inputStyle = { background: '#0f1e2d', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 2, padding: '0.75rem 1rem', color: '#e8f0f8', fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 300, outline: 'none', width: '100%' };
const btnStyle = { width: '100%', background: '#00AEEF', color: '#080f18', fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: '0.9rem', padding: '0.85rem', border: 'none', borderRadius: 2, cursor: 'pointer' };
const googleBtnStyle = { width: '100%', background: 'transparent', color: '#e8f0f8', fontFamily: "'DM Sans', sans-serif", fontWeight: 400, fontSize: '0.9rem', padding: '0.85rem', border: '0.5px solid rgba(255,255,255,0.12)', borderRadius: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem' };

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18">
    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
    <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
  </svg>
);

export default function Auth() {
  const [tab, setTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regCode, setRegCode] = useState('');
  const [resetEmail, setResetEmail] = useState('');

  const saveUserProfile = async (uid, name, email, role, companyCode, companyName) => {
    console.log('saveUserProfile called:', { uid, name, email, role, companyCode, companyName });
    try {
      await setDoc(doc(db, 'users', uid, 'latest', 'profile'), {
        name, email, role,
        companyCode: companyCode || '',
        companyName: companyName || '',
        onboardingComplete: false,
        createdAt: serverTimestamp()
      });
      console.log('Profile saved successfully');
    } catch (e) {
      console.error('Error saving profile:', e.code, e.message);
      throw e;
    }
    if (companyCode) {
      try {
        await setDoc(doc(db, 'companies', companyCode, 'members', uid), {
          uid, role, joinedAt: serverTimestamp()
        });
        console.log('Company member saved successfully');
      } catch (e) {
        console.error('Error saving company member:', e.code, e.message);
        throw e;
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, loginEmail.trim().toLowerCase(), loginPassword);
    } catch (err) { setError(getErrorMessage(err.code)); }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    setError(''); setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const profileSnap = await getDoc(doc(db, 'users', user.uid, 'latest', 'profile'));
      if (!profileSnap.exists()) {
        if (user.email.toLowerCase() === LEVARE_ADMIN_EMAIL) {
          await saveUserProfile(user.uid, user.displayName || '', user.email, 'admin', '', 'Levare Wellbeing');
        } else {
          await auth.signOut();
          setError('New accounts via Google must register with a company code. Please use the Register tab.');
          setLoading(false); return;
        }
      }
    } catch (err) { setError(getErrorMessage(err.code)); }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    if (regPassword !== regConfirm) { setError('Passwords do not match.'); setLoading(false); return; }
    if (regPassword.length < 8) { setError('Password must be at least 8 characters.'); setLoading(false); return; }
    const email = regEmail.trim().toLowerCase();

    // Levare admin bypass
    if (email === LEVARE_ADMIN_EMAIL) {
      try {
        const cred = await createUserWithEmailAndPassword(auth, email, regPassword);
        await saveUserProfile(cred.user.uid, regName.trim(), email, 'admin', '', 'Levare Wellbeing');
      } catch (err) { setError(getErrorMessage(err.code)); }
      setLoading(false); return;
    }

    if (!regCode.trim()) { setError('Please enter your company code.'); setLoading(false); return; }

    try {
      // Step 1: Look up company code FIRST (codeLookup is public, no auth needed)
      console.log('Looking up company code:', regCode);
      const codeResult = await lookupCompanyCode(regCode);
      if (!codeResult) {
        setError('Invalid company code. Please check with your HR team or Levare.');
        setLoading(false); return;
      }
      console.log('Code valid:', codeResult);

      // Step 2: Create auth account
      console.log('Attempting to create account for:', email);
      const cred = await createUserWithEmailAndPassword(auth, email, regPassword);
      console.log('Account created:', cred.user.uid);

      // Step 3: Get ID token and send to server to write profile
      const idToken = await cred.user.getIdToken(true);
      console.log('Got ID token, calling /api/register...');

      const apiBase = process.env.REACT_APP_API_URL || 'https://levare-platform.onrender.com';
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30000);
      let registerRes;
      try {
        registerRes = await fetch(apiBase + '/api/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            idToken,
            uid: cred.user.uid,
            name: regName.trim(),
            email,
            role: codeResult.role,
            companyCode: codeResult.companyCode,
            companyName: codeResult.companyName
          }),
          signal: controller.signal
        });
      } catch (fetchErr) {
        clearTimeout(timeout);
        if (fetchErr.name === 'AbortError') throw new Error('Server is starting up — please try again in 30 seconds.');
        throw fetchErr;
      }
      clearTimeout(timeout);

      if (!registerRes.ok) {
        const err = await registerRes.json();
        throw new Error(err.error || 'Registration failed');
      }

      console.log('Profile written successfully via server!');
      // Sign out and back in to get clean session
      await auth.signOut();
      await signInWithEmailAndPassword(auth, email, regPassword);
      window.location.reload();

    } catch (err) {
      console.error('Registration error:', err.code, err.message, err);
      setError(getErrorMessage(err.code) + ' (Code: ' + (err.code || 'unknown') + ')');
    }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail.trim().toLowerCase());
      setSuccess('Password reset email sent. Please check your inbox.');
      setShowReset(false);
    } catch (err) { setError(getErrorMessage(err.code)); }
    setLoading(false);
  };

  const ErrorMsg = ({ msg }) => msg ? <div style={{ background: 'rgba(240,80,80,0.1)', border: '0.5px solid #f05050', color: '#f05050', padding: '0.75rem 1rem', borderRadius: 2, fontSize: '0.82rem', marginBottom: '1rem' }}>{msg}</div> : null;
  const SuccessMsg = ({ msg }) => msg ? <div style={{ background: 'rgba(0,200,150,0.1)', border: '0.5px solid #00c896', color: '#00c896', padding: '0.75rem 1rem', borderRadius: 2, fontSize: '0.82rem', marginBottom: '1rem' }}>{msg}</div> : null;
  const Divider = () => <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '1.2rem 0', color: '#3d5a72', fontSize: '0.78rem' }}><div style={{ flex: 1, height: '0.5px', background: 'rgba(255,255,255,0.06)' }} />or<div style={{ flex: 1, height: '0.5px', background: 'rgba(255,255,255,0.06)' }} /></div>;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: '#080f18', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @media (max-width: 768px) {
          .auth-left-panel { display: none !important; }
          .auth-right-panel { width: 100% !important; padding: 2rem 1.5rem !important; }
        }
      `}</style>
      <div style={{ flex: 1, background: '#0f1e2d', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '3rem', borderRight: '0.5px solid rgba(255,255,255,0.06)' }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.4rem', color: '#e8f0f8' }}>Levare<span style={{ color: '#00AEEF' }}>.</span></div>
        <div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '2.2rem', fontWeight: 500, color: '#e8f0f8', lineHeight: 1.2, marginBottom: '1rem' }}>Health intelligence,<br /><em style={{ fontStyle: 'italic', color: '#00AEEF' }}>built for the workplace.</em></div>
          <p style={{ fontSize: '0.9rem', color: '#7a9ab8', fontWeight: 300, lineHeight: 1.7, marginBottom: '2rem' }}>Your personal health advisor and population health platform — all in one place.</p>
          {['36 biomarkers tracked across 8 categories','Rico AI advisor with full health context','HR population health dashboard','PDF blood test extraction','Wearable data integration'].map(item => (
            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', fontSize: '0.82rem', color: '#7a9ab8', marginBottom: '0.6rem' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00AEEF', flexShrink: 0 }} />{item}
            </div>
          ))}
        </div>
        <div style={{ fontSize: '0.75rem', color: '#3d5a72' }}>© 2025 Levare Wellbeing</div>
      </div>

      <div className="auth-right-panel" style={{ width: 480, maxWidth: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '3rem', overflowY: 'auto' }}>
        {showReset ? (
          <>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', fontWeight: 500, color: '#e8f0f8', marginBottom: '0.5rem' }}>Reset password</div>
            <p style={{ fontSize: '0.85rem', color: '#7a9ab8', marginBottom: '2rem', fontWeight: 300 }}>Enter your email and we'll send a reset link.</p>
            <ErrorMsg msg={error} /><SuccessMsg msg={success} />
            <form onSubmit={handleReset}>
              <div style={groupStyle}><label style={labelStyle}>Email Address</label><input style={inputStyle} type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} placeholder="you@company.com" required /></div>
              <button style={btnStyle} type="submit" disabled={loading}>{loading ? 'Sending...' : 'Send Reset Link'}</button>
            </form>
            <span style={{ fontSize: '0.78rem', color: '#7a9ab8', cursor: 'pointer', marginTop: '1.2rem', display: 'block', textAlign: 'center' }} onClick={() => { setShowReset(false); setError(''); }}>← Back to sign in</span>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', borderBottom: '0.5px solid rgba(255,255,255,0.06)', marginBottom: '2rem' }}>
              {[['login', 'Sign In'], ['register', 'Register']].map(([t, label]) => (
                <button key={t} onClick={() => { setTab(t); setError(''); setSuccess(''); }}
                  style={{ padding: '0.7rem 1.5rem', fontSize: '0.85rem', color: tab === t ? '#00AEEF' : '#7a9ab8', cursor: 'pointer', border: 'none', background: 'none', borderBottom: tab === t ? '2px solid #00AEEF' : '2px solid transparent', marginBottom: '-0.5px', fontFamily: "'DM Sans', sans-serif", transition: 'color 0.2s' }}>{label}
                </button>
              ))}
            </div>
            <ErrorMsg msg={error} /><SuccessMsg msg={success} />
            {tab === 'login' ? (
              <>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', fontWeight: 500, color: '#e8f0f8', marginBottom: '0.5rem' }}>Welcome back</div>
                <p style={{ fontSize: '0.85rem', color: '#7a9ab8', marginBottom: '2rem', fontWeight: 300 }}>Sign in to your Levare account.</p>
                <form onSubmit={handleLogin}>
                  <div style={groupStyle}><label style={labelStyle}>Email Address</label><input style={inputStyle} type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="you@company.com" required /></div>
                  <div style={groupStyle}><label style={labelStyle}>Password</label><input style={inputStyle} type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} placeholder="••••••••" required /></div>
                  <span style={{ fontSize: '0.78rem', color: '#7a9ab8', cursor: 'pointer', display: 'block', textAlign: 'right', marginBottom: '1.2rem' }} onClick={() => { setShowReset(true); setError(''); }}>Forgot password?</span>
                  <button style={btnStyle} type="submit" disabled={loading}>{loading ? 'Signing in...' : 'Sign In'}</button>
                </form>
                <Divider />
                <button style={googleBtnStyle} onClick={handleGoogleLogin} disabled={loading}><GoogleIcon /> Continue with Google</button>
              </>
            ) : (
              <>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', fontWeight: 500, color: '#e8f0f8', marginBottom: '0.5rem' }}>Create your account</div>
                <p style={{ fontSize: '0.85rem', color: '#7a9ab8', marginBottom: '2rem', fontWeight: 300 }}>You'll need a company code provided by your HR team or Levare.</p>
                <form onSubmit={handleRegister}>
                  <div style={groupStyle}><label style={labelStyle}>Full Name</label><input style={inputStyle} type="text" value={regName} onChange={e => setRegName(e.target.value)} placeholder="Your full name" required /></div>
                  <div style={groupStyle}><label style={labelStyle}>Email Address</label><input style={inputStyle} type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} placeholder="you@company.com" required /></div>
                  <div style={groupStyle}>
                    <label style={labelStyle}>Company Code</label>
                    <input style={inputStyle} type="text" value={regCode} onChange={e => setRegCode(e.target.value.toUpperCase())} placeholder="Enter your company code" />
                    <span style={{ fontSize: '0.72rem', color: '#3d5a72', marginTop: '0.3rem' }}>Provided by your HR team or Levare Wellbeing</span>
                  </div>
                  <div style={groupStyle}><label style={labelStyle}>Password</label><input style={inputStyle} type="password" value={regPassword} onChange={e => setRegPassword(e.target.value)} placeholder="Min. 8 characters" required /></div>
                  <div style={groupStyle}><label style={labelStyle}>Confirm Password</label><input style={inputStyle} type="password" value={regConfirm} onChange={e => setRegConfirm(e.target.value)} placeholder="Repeat your password" required /></div>
                  <button style={btnStyle} type="submit" disabled={loading}>{loading ? 'Creating account...' : 'Create Account'}</button>
                </form>
                <Divider />
                <button style={googleBtnStyle} onClick={handleGoogleLogin} disabled={loading}><GoogleIcon /> Continue with Google</button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
